/**
 * Returns `#1A202C` (dark) or `#ffffff` (white) — whichever yields the
 * higher contrast against `bgColor` per WCAG relative luminance.
 *
 * Why luminance and not HSL lightness?
 *   Pure red `#FF0000` has HSL lightness 50% but a relative luminance of
 *   ~0.21 (red contributes only 0.2126 of the perceived brightness),
 *   which means dark text on pure red has a contrast ratio of ~3:1 —
 *   below the WCAG AA threshold. Switching to luminance puts the
 *   threshold where the eye actually sees it, so saturated reds,
 *   purples, blues, and crimsons correctly pair with white text while
 *   yellows, limes, and cyans stay paired with dark text.
 *
 * `opacity < 0.7` (used by the digraph's faded-impact node tint) keeps
 * the dark-text shortcut because faded fills sit on a white card and
 * the effective background reads as pale regardless of the underlying
 * type color.
 */
function getContrastingFontColor(bgColor: string, opacity?: number): string {
  if (opacity !== undefined && opacity < 0.7) {
    return '#1A202C'
  }
  const luminance = relativeLuminance(bgColor)
  // 0.179 corresponds to the WCAG-recommended L* midpoint
  // (sqrt(1.05 * 0.05) - 0.05) ≈ 0.1791. Above that, dark text wins;
  // below, white wins. Calibrated against typical Chakra palette
  // 500-tone shades so users picking pure red/blue/purple get white.
  return luminance > 0.179 ? '#1A202C' : '#ffffff'
}

function relativeLuminance(hexColor: string): number {
  const r = sRGBChannel(parseInt(hexColor.slice(1, 3), 16) / 255)
  const g = sRGBChannel(parseInt(hexColor.slice(3, 5), 16) / 255)
  const b = sRGBChannel(parseInt(hexColor.slice(5, 7), 16) / 255)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** sRGB → linear RGB per the WCAG 2.x definition. */
function sRGBChannel(channel: number): number {
  return channel <= 0.03928
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4)
}

export default getContrastingFontColor
